import { useState } from 'react';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const steps = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Book the Free Call',
    duration: 'Day 0 — 30 mins',
    short: 'No pitch. Just the truth.',
    description:
      "Fill out the form and we'll schedule a straight-talking 30-minute call. No sales deck, no pressure. We look at your current online presence, run a live competitor check, and give you an honest picture of where you stand — and what it would take to get on top.",
    deliverable: 'What you get: A clear gap analysis and our honest recommendation — even if that recommendation is "you don\'t need us yet."',
  },
  {
    number: '02',
    icon: 'ri-microscope-line',
    title: 'We Audit Your Market',
    duration: 'Days 1–5',
    short: 'Deep intel before we move.',
    description:
      "Before we build anything, we go deep. Full competitor analysis, keyword landscape, technical audit of your existing site, local search presence review. We map every gap, every opportunity, and every weak point in your market — then we build a strategy around it.",
    deliverable: 'What you get: A full written strategy document with a prioritised action plan, tailored to your business and your market.',
  },
  {
    number: '03',
    icon: 'ri-tools-line',
    title: 'We Build Your Weapon',
    duration: 'Weeks 2–4',
    short: 'Custom. Fast. Built to convert.',
    description:
      "Custom website, AI integrations, SEO foundations, content systems — whatever your package includes, we build it properly. You get regular progress updates, not radio silence. Every decision is run past you. Nothing goes live without your sign-off.",
    deliverable: 'What you get: A fully built, tested, and optimised digital presence — ready to launch and built to rank.',
  },
  {
    number: '04',
    icon: 'ri-rocket-2-line',
    title: 'Launch & Dominate',
    duration: 'Week 4 onwards',
    short: 'Live. Ranking. Growing.',
    description:
      "We launch, monitor, and keep pushing. Rankings don't peak on day one — they build. We track everything, report in plain English every month, and keep refining the strategy as results roll in. Your competitors start feeling it. You start winning.",
    deliverable: 'What you get: Ongoing reporting, monthly strategy calls, and a partner who\'s as invested in your results as you are.',
  },
];

export default function ProcessSection() {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const groupRef = useScrollRevealGroup();

  return (
    <section id="process" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      {/* Top accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red/60 to-transparent" />
      {/* Grid */}
      <div className="absolute inset-0 hero-grid opacity-15 pointer-events-none" />
      {/* Ambient */}
      <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-ds-red/5 blur-[150px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-20 reveal">
          <span className="section-label">The Process</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-none">
              WHAT HAPPENS
              <br />
              <span className="text-ds-red neon-text-red">NEXT.</span>
            </h2>
            <div className="max-w-xs lg:text-right">
              <p className="font-body text-white/50 text-sm leading-relaxed">
                From first call to live and ranking — most clients are up and running in 4–6 weeks.
              </p>
              <p className="font-display text-ds-red text-xs tracking-widest mt-2">
                NO SURPRISES. NO DELAYS.
              </p>
            </div>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red" style={{ boxShadow: '0 0 12px rgba(255,23,68,0.5)' }} />
        </div>

        {/* Steps — desktop horizontal timeline */}
        <div className="hidden lg:block reveal">
          {/* Connector line */}
          <div className="relative mb-0">
            <div className="absolute top-[52px] left-[calc(12.5%+32px)] right-[calc(12.5%+32px)] h-px bg-ds-border z-0">
              <div className="h-full bg-gradient-to-r from-ds-red/60 via-ds-red/30 to-ds-red/10" />
            </div>

            <div className="grid grid-cols-4 gap-0 relative z-10">
              {steps.map((step, i) => (
                <div
                  key={step.number}
                  className="flex flex-col items-center text-center px-6 cursor-default group"
                  onMouseEnter={() => setActiveStep(i)}
                  onMouseLeave={() => setActiveStep(null)}
                >
                  {/* Icon node */}
                  <div
                    className={`w-16 h-16 flex items-center justify-center border transition-all duration-400 mb-6 relative z-10
                      ${activeStep === i
                        ? 'border-ds-red bg-ds-red/15'
                        : 'border-ds-border bg-ds-bg group-hover:border-ds-red/60'
                      }`}
                    style={activeStep === i ? { boxShadow: '0 0 30px rgba(180,255,57,0.25)' } : {}}
                  >
                    <i className={`${step.icon} text-2xl transition-colors duration-300 ${activeStep === i ? 'text-ds-red' : 'text-white/30 group-hover:text-ds-red/70'}`} />
                    {/* Step number dot */}
                    <div
                      className={`absolute -top-2.5 -right-2.5 w-6 h-6 flex items-center justify-center text-xs font-display transition-all duration-300
                        ${activeStep === i ? 'bg-ds-red text-white' : 'bg-ds-bg-3 text-white/40 border border-ds-border'}`}
                    >
                      {step.number}
                    </div>
                  </div>

                  {/* Duration tag */}
                  <span className={`font-body text-xs tracking-wider mb-2 transition-colors duration-300 ${activeStep === i ? 'text-ds-red' : 'text-white/30'}`}>
                    {step.duration}
                  </span>

                  {/* Title */}
                  <h3 className={`font-display text-lg text-white leading-tight mb-2 tracking-wide transition-colors duration-300 ${activeStep === i ? 'text-white' : 'text-white/80'}`}>
                    {step.title}
                  </h3>

                  {/* Short */}
                  <p className={`font-body text-xs transition-colors duration-300 ${activeStep === i ? 'text-white/70' : 'text-white/35'}`}>
                    {step.short}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Expanded detail panel */}
          <div className="mt-8 min-h-[180px]">
            {activeStep !== null ? (
              <div
                key={activeStep}
                className="border border-ds-red/30 bg-ds-bg-2 p-8 grid lg:grid-cols-3 gap-8"
                style={{ animation: 'fadeSlideIn 0.25s ease forwards', boxShadow: '0 0 40px rgba(180,255,57,0.06)' }}
              >
                {/* Step label */}
                <div className="flex flex-col justify-between">
                  <div>
                    <span className="font-display text-ds-red text-xs tracking-[0.3em]">STEP {steps[activeStep].number}</span>
                    <h3 className="font-display text-3xl text-white mt-1 tracking-wide">{steps[activeStep].title}</h3>
                  </div>
                  <div className="h-px w-16 bg-ds-red mt-4" style={{ boxShadow: '0 0 8px rgba(180,255,57,0.4)' }} />
                </div>

                {/* Description */}
                <div>
                  <p className="font-body text-white/60 text-sm leading-relaxed">
                    {steps[activeStep].description}
                  </p>
                </div>

                {/* Deliverable */}
                <div className="border-l border-ds-border pl-8">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-gift-line text-ds-red text-xs" />
                    </div>
                    <span className="font-body text-white/30 text-xs tracking-widest uppercase">Deliverable</span>
                  </div>
                  <p className="font-body text-white/70 text-sm leading-relaxed italic">
                    {steps[activeStep].deliverable}
                  </p>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-ds-border/40 p-8 text-center">
                <p className="font-body text-white/20 text-xs tracking-widest uppercase">
                  Hover a step to see what happens
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Steps — mobile vertical */}
        <div className="lg:hidden space-y-0 reveal">
          {steps.map((step, i) => (
            <div key={step.number} className="relative">
              {/* Vertical connector */}
              {i < steps.length - 1 && (
                <div className="absolute left-7 top-[72px] bottom-0 w-px bg-gradient-to-b from-ds-red/40 to-ds-border/30 z-0" />
              )}

              <div className="relative z-10 flex gap-6 pb-10">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div
                    className="w-14 h-14 flex items-center justify-center border border-ds-border bg-ds-bg-2 relative"
                  >
                    <i className={`${step.icon} text-ds-red text-xl`} />
                    <div className="absolute -top-2 -right-2 w-5 h-5 bg-ds-red flex items-center justify-center">
                      <span className="font-display text-white text-xs">{step.number}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 pt-1">
                  <span className="font-body text-ds-red text-xs tracking-wider">{step.duration}</span>
                  <h3 className="font-display text-xl text-white mt-1 mb-2 tracking-wide">{step.title}</h3>
                  <p className="font-body text-white/55 text-sm leading-relaxed mb-3">{step.description}</p>
                  <div className="border-l-2 border-ds-red/40 pl-4">
                    <p className="font-body text-white/50 text-xs leading-relaxed italic">{step.deliverable}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="grid sm:grid-cols-3 gap-px bg-ds-border">
          {[
            { icon: 'ri-timer-line', label: '4–6 Weeks', sub: 'From call to live' },
            { icon: 'ri-shield-check-line', label: 'No Lock-In', sub: 'Month-to-month always' },
            { icon: 'ri-headphone-line', label: 'Dedicated Support', sub: 'A real person, every time' },
          ].map((item) => (
            <div key={item.label} className="bg-ds-bg-2 px-6 py-5 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center border border-ds-red/30 flex-shrink-0">
                <i className={`${item.icon} text-ds-red text-lg`} />
              </div>
              <div>
                <p className="font-display text-white text-base tracking-wide">{item.label}</p>
                <p className="font-body text-white/40 text-xs mt-0.5">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>

      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
